import { useCallback, useEffect, useMemo, useState } from "react";
import {
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import type {
  ClinicalCrmLayoutStorage,
  ClinicalCrmSectionId,
  ClinicalCrmSectionsLayoutModel,
} from "../types";

const STORAGE_KEY = "clinical-crm-layout-v1";

/** Ordem padrão: templates, comunicações, estatísticas e WhatsApp (pós-consulta via dialog). */
const DEFAULT_SECTION_ORDER: ClinicalCrmSectionId[] = [
  "automations",
  "content",
  "communications",
  "statistics",
  "whatsapp",
];

const DEFAULT_COLLAPSED_MAP: Record<ClinicalCrmSectionId, boolean> = {
  automations: false,
  content: false,
  communications: false,
  postAttendance: false,
  statistics: false,
  whatsapp: false,
};

const ALL_SECTION_IDS = new Set<ClinicalCrmSectionId>(DEFAULT_SECTION_ORDER);

function isClinicalCrmSectionId(value: unknown): value is ClinicalCrmSectionId {
  return (
    typeof value === "string" &&
    ALL_SECTION_IDS.has(value as ClinicalCrmSectionId)
  );
}

/** Remove seção legada que migrou para dialog no acesso rápido. */
function withoutPostAttendanceSection(
  order: ClinicalCrmSectionId[],
): ClinicalCrmSectionId[] {
  return order.filter((id) => id !== "postAttendance");
}

/**
 * Mescla ordem salva com IDs novos sem descartar preferências antigas.
 */
function mergeSectionOrder(stored: unknown): ClinicalCrmSectionId[] {
  if (!Array.isArray(stored)) {
    return DEFAULT_SECTION_ORDER;
  }

  const knownIds = withoutPostAttendanceSection(
    stored.filter(isClinicalCrmSectionId),
  );
  if (knownIds.length === 0) {
    return DEFAULT_SECTION_ORDER;
  }

  const missingIds = DEFAULT_SECTION_ORDER.filter(
    (id) => !knownIds.includes(id),
  );
  if (
    missingIds.length === 0 &&
    knownIds.length === DEFAULT_SECTION_ORDER.length
  ) {
    return knownIds;
  }

  return withoutPostAttendanceSection([...knownIds, ...missingIds]);
}

function mergeCollapsedMap(
  stored: Partial<ClinicalCrmLayoutStorage>["collapsedMap"],
): Record<ClinicalCrmSectionId, boolean> {
  return {
    automations: Boolean(stored?.automations),
    content: Boolean(stored?.content),
    communications: Boolean(stored?.communications),
    postAttendance: Boolean(stored?.postAttendance),
    statistics: Boolean(stored?.statistics),
    whatsapp: Boolean(stored?.whatsapp),
  };
}

function loadFromStorage(): ClinicalCrmLayoutStorage {
  if (typeof window === "undefined") {
    return {
      sectionOrder: DEFAULT_SECTION_ORDER,
      collapsedMap: DEFAULT_COLLAPSED_MAP,
    };
  }

  try {
    const rawStorage = window.localStorage.getItem(STORAGE_KEY);
    if (!rawStorage) {
      return {
        sectionOrder: DEFAULT_SECTION_ORDER,
        collapsedMap: DEFAULT_COLLAPSED_MAP,
      };
    }

    const parsedStorage = JSON.parse(
      rawStorage,
    ) as Partial<ClinicalCrmLayoutStorage>;

    return {
      sectionOrder: mergeSectionOrder(parsedStorage.sectionOrder),
      collapsedMap: mergeCollapsedMap(parsedStorage.collapsedMap),
    };
  } catch {
    return {
      sectionOrder: DEFAULT_SECTION_ORDER,
      collapsedMap: DEFAULT_COLLAPSED_MAP,
    };
  }
}

export function useClinicalCrmSectionsLayout(): ClinicalCrmSectionsLayoutModel {
  const initialLayout = useMemo(() => loadFromStorage(), []);
  const [sectionOrder, setSectionOrder] = useState<ClinicalCrmSectionId[]>(
    initialLayout.sectionOrder,
  );
  const [collapsedMap, setCollapsedMap] = useState<
    Record<ClinicalCrmSectionId, boolean>
  >(initialLayout.collapsedMap);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleToggleSectionCollapsed = useCallback(
    (sectionId: ClinicalCrmSectionId) => {
      setCollapsedMap((previousMap) => ({
        ...previousMap,
        [sectionId]: !previousMap[sectionId],
      }));
    },
    [],
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSectionOrder((previousOrder) => {
      const oldIndex = previousOrder.findIndex(
        (sectionId) => sectionId === active.id,
      );
      const newIndex = previousOrder.findIndex(
        (sectionId) => sectionId === over.id,
      );
      if (oldIndex < 0 || newIndex < 0) return previousOrder;
      return arrayMove(previousOrder, oldIndex, newIndex);
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload: ClinicalCrmLayoutStorage = { sectionOrder, collapsedMap };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [collapsedMap, sectionOrder]);

  return {
    sectionOrder,
    collapsedMap,
    sensors,
    collisionDetection: closestCenter,
    sortingStrategy: verticalListSortingStrategy,
    handleDragEnd,
    handleToggleSectionCollapsed,
  };
}
